// Типы и интерфейсы для AML-модуля (Anti-Money Laundering)

// ─── Перечисления ─────────────────────────────────────────────────────────────

export enum AmlCaseStatus {
  PENDING    = 'pending',    // Ожидает рассмотрения
  IN_REVIEW  = 'in_review',  // На проверке у оператора
  APPROVED   = 'approved',   // Операция разрешена
  REJECTED   = 'rejected',   // Операция отклонена / аккаунт заблокирован
  ESCALATED  = 'escalated',  // Эскалировано в комплаенс
  OVERDUE    = 'overdue',    // Просрочено (срок рассмотрения истёк)
}

export enum AmlRiskLevel {
  LOW      = 'low',
  MEDIUM   = 'medium',
  HIGH     = 'high',
  CRITICAL = 'critical',
}

export type DecisionType = 'approve' | 'reject' | 'escalate' | 'request_info';

export type DecisionReasonCode =
  | 'false_positive'
  | 'normal_activity'
  | 'verified_source'
  | 'confirmed_aml'
  | 'sanctions_match'
  | 'suspicious_pattern'
  | 'pep_risk'
  | 'need_documents'
  | 'need_source_of_funds'
  | 'need_explanation'
  | 'requires_compliance';

// ─── Справочник правил ────────────────────────────────────────────────────────

export interface AmlRule {
  id: string;
  code: string;
  name: string;
  description: string;
  category: 'transaction' | 'pattern' | 'geo' | 'crypto' | 'profile' | 'kyc' | 'sanctions' | 'behavior' | 'counterparty';
  severity: 'low' | 'medium' | 'high' | 'critical';
  threshold?: number;
  currency?: string;
  isActive: boolean;
}

// ─── Сработавшее правило ──────────────────────────────────────────────────────

export interface TriggeredRule {
  ruleId: string;
  ruleCode: string;
  ruleName: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  triggeredAt: string;          // ISO 8601
  details: string;              // Человекочитаемое описание
  transactionId?: string;
  amount?: number;
  currency?: string;
}

// ─── Транзакция (упрощённая для AML) ─────────────────────────────────────────

export interface AmlTransaction {
  id: string;
  amount: number;
  currency: string;
  type: 'deposit' | 'withdrawal' | 'transfer' | 'exchange' | 'crypto_withdrawal' | 'crypto_deposit';
  direction: 'in' | 'out';
  status: 'pending' | 'completed' | 'blocked' | 'failed';
  createdAt: string;
  accountId: string;
  counterpartyName?: string;
  counterpartyAccount?: string;
  description?: string;
}

// ─── Детализация скора ────────────────────────────────────────────────────────

export interface ScoreFactorDetail {
  factor: string;
  score: number;       // 0-100
  weight: number;      // 0.0-1.0
  description: string;
}

export interface ScoringBreakdown {
  total: number;             // Итоговый скор 0-100
  kycScore: number;          // Скор от KYC-сервиса
  transactionScore: number;  // Транзакционный риск
  behaviorScore: number;     // Поведенческий риск
  geoScore: number;          // Географический риск
  details: ScoreFactorDetail[];
}

// ─── Событие таймлайна ────────────────────────────────────────────────────────

export type TimelineEventType =
  | 'case_created'
  | 'rule_triggered'
  | 'assigned'
  | 'comment'
  | 'info_requested'
  | 'info_received'
  | 'decision_made'
  | 'escalated'
  | 'transaction_blocked'
  | 'overdue';

export interface TimelineEvent {
  id: string;
  eventType: TimelineEventType;
  description: string;
  occurredAt: string;   // ISO 8601
  actor: string;        // ID актора ('system' | operatorId)
  actorName?: string;
  metadata?: Record<string, unknown>;
}

// ─── Решение оператора ────────────────────────────────────────────────────────

export interface AmlDecision {
  id: string;
  caseId: string;
  decision: DecisionType;
  reasonCode: DecisionReasonCode;
  comment: string;
  operatorId: string;
  operatorName: string;
  decidedAt: string;  // ISO 8601
  attachments?: string[];
}

// ─── Кейс AML ─────────────────────────────────────────────────────────────────

export interface AmlCase {
  id: string;                          // UUID (для API-вызовов)
  caseId?: string;                     // Формат: AML-YYYY-NNNN (для отображения)
  userId: string;
  userEmail: string;
  userName: string;
  userPhone?: string;
  status: AmlCaseStatus;
  riskLevel: AmlRiskLevel;
  riskScore: number;                   // 0-100
  assignedTo?: string;                 // operatorId
  assignedToName?: string;
  rulesCount?: number;                 // Кол-во сработавших правил (из списка)
  triggeredRules: TriggeredRule[];
  transaction: AmlTransaction;
  scoringBreakdown: ScoringBreakdown;
  timeline: TimelineEvent[];
  decision?: AmlDecision;
  createdAt: string;
  updatedAt: string;
  dueAt: string;                       // Срок рассмотрения
  // Профиль пользователя (из существующих эндпоинтов)
  kycStatus?: 'approved' | 'pending' | 'rejected' | 'not_started';
  accountAge?: number;                 // Дней с регистрации
  totalTransactions30d?: number;
  totalVolume30d?: number;
  country?: string;
}

// ─── Фильтры таблицы ──────────────────────────────────────────────────────────

// v48 UX-1: spec-значение 'active' = pending + in_review (соответствует
// MenuBadgeProvider count). Backend repository.ListAlerts расширен на
// `status='active'` → WHERE status IN ('pending','in_review').
export type AmlStatusFilter = AmlCaseStatus | 'active' | '';

export interface AmlFilter {
  status?: AmlStatusFilter;
  riskLevel?: AmlRiskLevel | '';
  assignedTo?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;      // По userId, email, имени, id кейса
  page: number;
  size: number;
}

// ─── Статистика для заголовка ─────────────────────────────────────────────────

export interface AmlStats {
  total: number;
  pending: number;
  inReview: number;
  approved: number;
  rejected: number;
  escalated: number;
  overdue: number;
  avgResolutionHours: number;
  criticalCount: number;
}

// ─── Ответы API ───────────────────────────────────────────────────────────────

export interface AmlCasesResponse {
  data: AmlCase[];
  total: number;
  page: number;
  size: number;
}

// ─── Форма принятия решения (для AmlDecisionModal) ────────────────────────────

export interface DecisionFormState {
  decision: DecisionType | '';
  reasonCode: DecisionReasonCode | '';
  comment: string;
}

// ─── История провайдеров (Marble / Crystal) ───────────────────────────────────
// Заполняется после получения proto от команды scoring.
// source: string — не union, чтобы не ломать при добавлении новых провайдеров.

export type ProviderHistoryStatus = 'passed' | 'failed' | 'inconclusive' | 'error';

export interface ProviderHistoryEntry {
  id: string;
  source: string;                  // 'marble' | 'crystal' | ... (расширяемо)
  sourceDisplayName?: string;      // Человекочитаемое название провайдера
  occurredAt: string;              // ISO 8601 — когда провайдер ответил
  refId?: string;                  // ID запроса на стороне провайдера
  status: ProviderHistoryStatus;
  summary: string;                 // Краткая сводка для строки таблицы
  score?: number;                  // Числовой скор: Marble score (0-100) или Crystal risk_score
  payload: unknown;                // Raw ответ — рендерится как JSON
  durationMs?: number;             // Время ответа провайдера (мс)
}

export interface ProviderHistoryResponse {
  items: ProviderHistoryEntry[];
  total: number;
  limit: number;
  offset: number;
}
